import { useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Sailboat, BookOpen, MessageSquareHeart, Users, Fish, Baby, LogOut, Shield, ScrollText, User, Highlighter , Crown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Emblem } from "./Emblem";
import { StreakChip } from "./Streak";
import { PageBackdrop } from "./PageBackdrop";
import { NotificationBell, UnreadDot } from "./Notifications";

const BACKDROPS: Record<string, string> = { "/shore": "shore", "/boat": "boat", "/deep-water": "deep", "/cast-net": "net", "/groups": "groups" };

export const NAV = [
  { to: "/shore", m: "Shore", l: "Home", icon: Sun, id: "shore" },
  { to: "/boat", m: "Boat", l: "Stories", icon: Sailboat, id: "boat" },
  { to: "/deep-water", m: "Deep Water", l: "Bible Study", icon: BookOpen, id: "deep-water" },
  { to: "/cast-net", m: "Cast Net", l: "Testimonies & Affirmations", icon: MessageSquareHeart, id: "cast-net" },
  { to: "/groups", m: "Groups", l: "Study Groups", icon: Users, id: "groups" },
];

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E293B]"><Fish className="h-4 w-4 text-[#FAF7F2]" /></span>
    <span className="font-display text-2xl font-bold tracking-tight text-slate-900">Cast</span>
  </Link>
);

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#C86D51]">
        <Emblem avatar={user.avatar} size={38} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5 text-sm font-semibold" data-testid="user-menu-username">{user.username}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-profile" onClick={() => nav(`/u/${user.username}`)}><User className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-verses" onClick={() => nav("/verses")}><Highlighter className="mr-2 h-4 w-4" />My verses</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-plans" onClick={() => nav("/plans")}><Highlighter className="mr-2 h-4 w-4" />Reading plans</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-plus" onClick={() => nav("/plus")}><Crown className="mr-2 h-4 w-4" />Cast Plus</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-parent" onClick={() => nav("/parent")}><Baby className="mr-2 h-4 w-4" />Parent dashboard</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-rules" onClick={() => nav("/rules")}><ScrollText className="mr-2 h-4 w-4" />Community rules</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><Shield className="mr-2 h-4 w-4" />Moderation</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    const r = localStorage.getItem("cast_return");
    if (r) { localStorage.removeItem("cast_return"); if (r !== loc.pathname) nav(r, { replace: true }); }
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-24 lg:pb-0">
      {BACKDROPS[loc.pathname] && <PageBackdrop variant={BACKDROPS[loc.pathname]} />}
      <header className="sticky top-0 z-50 border-b border-[#E2DBCE] bg-[#FAF7F2]/90 backdrop-blur-md print:hidden">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.id}`}
                className={({ isActive }) => `rounded-lg px-3 py-1.5 leading-tight transition-colors ${isActive ? "bg-[#1E293B] text-[#FAF7F2]" : "text-slate-700 hover:bg-[#F3ECE0]"}`}>
                <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">{n.m}</span>
                <span className="flex items-center text-sm font-semibold">{n.l}{n.id === "cast-net" && <UnreadDot testId="nav-cast-net-badge" />}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <StreakChip />
            <NotificationBell />
            <Link to="/parent" data-testid="toggle-kids-mode-button"
              className="hidden items-center gap-1.5 rounded-full border border-[#81B29A] bg-[#81B29A]/15 px-3.5 py-1.5 text-sm font-semibold text-[#3f6b55] transition-colors hover:bg-[#81B29A]/30 sm:flex">
              <Baby className="h-4 w-4" /> Kids Cove
            </Link>
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14"><Outlet /></main>
      <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 border-t border-[#E2DBCE] bg-white/95 backdrop-blur-md print:hidden lg:hidden">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.id}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold ${isActive ? "text-[#C86D51]" : "text-slate-500"}`}>
            <n.icon className="h-5 w-5" />
            {n.id === "cast-net" && <span className="absolute right-[22%] top-1"><UnreadDot testId="mobile-nav-cast-net-badge" /></span>}
            <span>{n.m}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
